import { Subscription } from "rxjs";
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  EventEmitter,
  Output,
} from "@angular/core";
import { FormControl, UntypedFormControl } from "@angular/forms";
import { TranslateService, LangChangeEvent } from "@ngx-translate/core";
import { FormHelperService } from "../../helper/form-helper.service";
import { GoogleMap, MapGeocoder } from "@angular/google-maps";
import { GemInputGoogleAddressSelect } from "../gem-input-google-address-select/gem-input-google-address-select.component";
import { MatDialog } from "@angular/material/dialog";
import { CookieHelperService } from "../../helper/cookie-helper.service";

export class GemInputGoogleAddressEvent {
  bounds: google.maps.LatLngBounds;
  markerPositions: google.maps.LatLngLiteral[];
}

@Component({
  selector: "gem-input-google-address",
  templateUrl: "./gem-input-google-address.component.html",
  styleUrls: ["./gem-input-google-address.component.scss"],
  standalone: false,
})
export class GemInputGoogleAddressComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() change = new EventEmitter();
  @ViewChild("streetNameElRef") streetNameElRef: ElementRef;

  @ViewChild(GoogleMap) map: GoogleMap;
  markerPositions: google.maps.LatLngLiteral[] = [];

  streetNameInputControl: UntypedFormControl;
  streetNameIsRequired: boolean;
  streetNameRequiredErrorMessage: string;

  streetNumberIsRequired: boolean;
  streetNumberRequiredErrorMessage: string;

  postalCodeInputControl: UntypedFormControl;
  postalCodeIsRequired: boolean;
  postalCodeRequiredErrorMessage: string;

  cityInputControl: UntypedFormControl;
  cityIsRequired: boolean;
  cityRequiredErrorMessage: string;

  countryInputControl: UntypedFormControl;
  countryIsRequired: boolean;
  countryRequiredErrorMessage: string;

  subscriptions: Subscription[] = [];

  @Input() streetNamePlaceholder: string;
  @Input() streetNameHint = "";
  @Input() streetNumberPlaceholder: string;
  @Input() streetNumberHint = "";
  @Input() postalCodePlaceholder: string;
  @Input() postalCodeHint = "";
  @Input() cityPlaceholder: string;
  @Input() cityHint = "";
  @Input() countryPlaceholder: string;
  @Input() countryHint = "";
  @Input() showCheckBoxes: boolean = false;
  @Input() streetCheckboxControl: FormControl;
  @Input() postalCodeCheckboxControl: FormControl;
  @Input() cityCheckboxControl: FormControl;
  @Input() countryCheckboxControl: FormControl;

  route: string = "";
  streetName: string = "";
  subLocalityLevel1: string = "";
  city: string = "";
  administrativeAreaLevel3: string = "";
  administrativeAreaLevel1: string = "";
  country: string = "";
  postalCode: string = "";

  @Input() set streetNameControl(controlObj: UntypedFormControl) {
    this.setControl(controlObj, "STREET_NAME");
  }

  get streetNameControl(): UntypedFormControl {
    return this.streetNameInputControl;
  }

  @Input() set streetNumberControl(controlObj: UntypedFormControl) {
    this.setControl(controlObj, "STREET_NUMBER");
  }

  @Input() set postalCodeControl(controlObj: UntypedFormControl) {
    this.setControl(controlObj, "POSTAL_CODE");
  }

  get postalCodeControl(): UntypedFormControl {
    return this.postalCodeInputControl;
  }

  @Input() set cityControl(controlObj: UntypedFormControl) {
    this.setControl(controlObj, "CITY");
  }

  get cityControl(): UntypedFormControl {
    return this.cityInputControl;
  }

  @Input() set countryControl(controlObj: UntypedFormControl) {
    this.setControl(controlObj, "COUNTRY");
  }

  get countryControl(): UntypedFormControl {
    return this.countryInputControl;
  }

  @Input() set triggerAddressChange(change: { component: string; change: boolean }) {
    if (change?.change) {
      this.onAddressChange("street");
    }
  }

  private setControl(controlObj: UntypedFormControl, type: string) {
    if (type === "STREET_NAME") {
      this.streetNameInputControl = controlObj;
      this.streetNameIsRequired = this.formHelper.checkRequiredValidator(
        this.streetNameInputControl,
      );
      this.streetName = controlObj.value;

      const placeholderInputValue = this.streetNamePlaceholder;
      const hintInputValue = this.streetNameHint;

      if (this.streetNamePlaceholder && this.streetNamePlaceholder !== "") {
        this.streetNamePlaceholder = this.translateService.instant(this.streetNamePlaceholder);
      }
      if (this.streetNameHint !== "") {
        this.streetNameHint = this.translateService.instant(this.streetNameHint);
      }

      const sub = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
        if (this.streetNamePlaceholder && this.streetNamePlaceholder !== "") {
          this.streetNamePlaceholder = this.translateService.instant(placeholderInputValue);
        }
        if (this.streetNameHint !== "") {
          this.streetNameHint = this.translateService.instant(hintInputValue);
        }
        this.streetNameRequiredErrorMessage = this.formHelper.createRequiredErrorMessage(
          this.streetNamePlaceholder,
        );
      });
      this.subscriptions.push(sub);

      this.streetNameRequiredErrorMessage = this.formHelper.createRequiredErrorMessage(
        this.streetNamePlaceholder,
      );
    } else if (type === "POSTAL_CODE") {
      this.postalCodeInputControl = controlObj;
      this.postalCodeIsRequired = this.formHelper.checkRequiredValidator(
        this.postalCodeInputControl,
      );
      this.postalCode = controlObj.value;

      const placeholderInputValue = this.postalCodePlaceholder;
      const hintInputValue = this.postalCodeHint;

      if (this.postalCodePlaceholder && this.postalCodePlaceholder !== "") {
        this.postalCodePlaceholder = this.translateService.instant(this.postalCodePlaceholder);
      }
      if (this.postalCodeHint !== "") {
        this.postalCodeHint = this.translateService.instant(this.postalCodeHint);
      }

      const sub = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
        if (this.postalCodePlaceholder && this.postalCodePlaceholder !== "") {
          this.postalCodePlaceholder = this.translateService.instant(placeholderInputValue);
        }
        if (this.postalCodeHint !== "") {
          this.postalCodeHint = this.translateService.instant(hintInputValue);
        }
        this.postalCodeRequiredErrorMessage = this.formHelper.createRequiredErrorMessage(
          this.postalCodePlaceholder,
        );
      });
      this.subscriptions.push(sub);

      this.postalCodeRequiredErrorMessage = this.formHelper.createRequiredErrorMessage(
        this.postalCodePlaceholder,
      );
    } else if (type === "CITY") {
      this.cityInputControl = controlObj;
      this.cityIsRequired = this.formHelper.checkRequiredValidator(this.cityInputControl);
      this.city = controlObj.value;

      const placeholderInputValue = this.cityPlaceholder;
      const hintInputValue = this.cityHint;

      if (this.cityPlaceholder && this.cityPlaceholder !== "") {
        this.cityPlaceholder = this.translateService.instant(this.cityPlaceholder);
      }
      if (this.cityHint !== "") {
        this.cityHint = this.translateService.instant(this.cityHint);
      }

      const sub = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
        if (this.cityPlaceholder && this.cityPlaceholder !== "") {
          this.cityPlaceholder = this.translateService.instant(placeholderInputValue);
        }
        if (this.cityHint !== "") {
          this.cityHint = this.translateService.instant(hintInputValue);
        }
        this.cityRequiredErrorMessage = this.formHelper.createRequiredErrorMessage(
          this.cityPlaceholder,
        );
      });
      this.subscriptions.push(sub);

      this.cityRequiredErrorMessage = this.formHelper.createRequiredErrorMessage(
        this.cityPlaceholder,
      );
    } else if (type === "COUNTRY") {
      this.countryInputControl = controlObj;
      this.countryIsRequired = this.formHelper.checkRequiredValidator(this.countryInputControl);
      this.country = controlObj.value;

      const placeholderInputValue = this.countryPlaceholder;
      const hintInputValue = this.countryHint;

      if (this.countryPlaceholder && this.countryPlaceholder !== "") {
        this.countryPlaceholder = this.translateService.instant(this.countryPlaceholder);
      }
      if (this.countryHint !== "") {
        this.countryHint = this.translateService.instant(this.countryHint);
      }

      const sub = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
        if (this.countryPlaceholder && this.countryPlaceholder !== "") {
          this.countryPlaceholder = this.translateService.instant(placeholderInputValue);
        }
        if (this.countryHint !== "") {
          this.countryHint = this.translateService.instant(hintInputValue);
        }
        this.countryRequiredErrorMessage = this.formHelper.createRequiredErrorMessage(
          this.countryPlaceholder,
        );
      });
      this.subscriptions.push(sub);

      this.countryRequiredErrorMessage = this.formHelper.createRequiredErrorMessage(
        this.countryPlaceholder,
      );
    }
  }

  constructor(
    private translateService: TranslateService,
    private formHelper: FormHelperService,
    private geocoder: MapGeocoder,
    public dialog: MatDialog,
    private cookieHelperService: CookieHelperService,
  ) {}

  ngOnInit(): void {
    const sub = this.cookieHelperService.getCookieChanges().subscribe(() => {
      if (this.isGoogleServicesConsent) {
        this.setAutocomplete();
      } else {
        if (this.streetNameElRef) {
          google.maps.event.clearInstanceListeners(this.streetNameElRef?.nativeElement);
        }
      }
    });
    this.subscriptions.push(sub);
  }

  ngAfterViewInit(): void {
    if (this.isGoogleServicesConsent) {
      this.setAutocomplete();
    }
  }

  onAddressChange(property: string): void {
    const search: string = this.getSearchAddress(property);
    this.geocoder
      .geocode({
        address: search,
      })
      .subscribe(({ results }) => {
        this.mapGooglePlaceToAddress(results);
      });
  }

  private getSearchAddress(property: string): string {
    const searchArray: string[] = [];
    if (property === "street") {
      if (this.streetName) {
        searchArray.push(this.streetName);
      }
      if (this.postalCode) {
        searchArray.push(this.postalCode);
      }
      if (this.city) {
        searchArray.push(this.city);
      }
      if (this.country) {
        searchArray.push(this.country);
      }
    } else if (property === "postalCode") {
      if (this.streetName) {
        searchArray.push(this.streetName);
      }
      if (this.postalCode) {
        searchArray.push(this.postalCode);
      }
      if (this.country) {
        searchArray.push(this.country);
      }
    } else if (property === "city") {
      if (this.streetName) {
        searchArray.push(this.streetName);
      }
      if (this.city) {
        searchArray.push(this.city);
      }
      if (this.country) {
        searchArray.push(this.country);
      }
    } else if (property === "country") {
      if (this.streetName) {
        searchArray.push(this.streetName);
      }
      if (this.postalCode) {
        searchArray.push(this.postalCode);
      }
      if (this.city) {
        searchArray.push(this.city);
      }
      if (this.country) {
        searchArray.push(this.country);
      }
    }
    return searchArray.join(",");
  }

  private setAutocomplete(): void {
    // Check if the element reference exists and is actually an HTMLInputElement
    if (
      !this.streetNameElRef ||
      !this.streetNameElRef.nativeElement ||
      !(this.streetNameElRef.nativeElement instanceof HTMLInputElement)
    ) {
      console.warn("Street name element reference is not available or not an HTMLInputElement");
      return;
    }

    const options: google.maps.places.AutocompleteOptions = {
      types: ["address"],
    };

    try {
      const searchBox = new google.maps.places.Autocomplete(
        this.streetNameElRef.nativeElement,
        options,
      );

      searchBox.addListener("place_changed", () => {
        const places: google.maps.places.PlaceResult[] = [];
        places.push(searchBox.getPlace());
        this.mapGooglePlaceToAddress(places);
      });
    } catch (error) {
      console.error("Error initializing Google Maps Autocomplete:", error);
    }
  }

  private mapGooglePlaceToAddress(places: google.maps.places.PlaceResult[]): void {
    if (places.length === 0) {
      return;
    }

    let place: google.maps.places.PlaceResult;

    if (places.length > 1) {
      const dialogRef = this.dialog.open(GemInputGoogleAddressSelect, {
        data: {
          places: places,
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        place = result;
        this.autocompleteFields(place);
      });
    } else {
      this.autocompleteFields(places[0]);
    }
  }

  private autocompleteFields(place: google.maps.places.PlaceResult): void {
    this.clearFields();

    if (!place?.address_components?.length) return;

    // Helper: get a component by type
    const get = (type: string) =>
      place.address_components!.find((c) => c.types.includes(type))?.long_name ?? "";

    // Collect pieces first (order-independent)
    const streetNumber = get("street_number");
    const route = get("route");
    const neighborhood = get("neighborhood");
    const sublocalityLevel1 = get("sublocality_level_1");
    const locality = get("locality");
    const sublocality = get("sublocality");
    const postalTown = get("postal_town"); // common in some countries (e.g. UK)
    const adminLevel3 = get("administrative_area_level_3");
    const adminLevel1 = get("administrative_area_level_1");
    const country = get("country");
    const postalCode = get("postal_code");

    // Street
    const hasRoute = !!route;
    if (hasRoute) {
      this.route = route;
      this.streetName = route;

      // Build street line once (includes number if available)
      let streetLine = [route, streetNumber].filter(Boolean).join(" ");

      // Optional extra detail
      if (neighborhood) streetLine += `, ${neighborhood}`;

      this.streetNameInputControl.setValue(streetLine);
    } else {
      this.streetNameElRef.nativeElement.value = "";
    }

    // City (prefer locality, fallback to postal_town, then sublocality)
    const city = locality || postalTown || sublocality;
    if (city) {
      this.city = city;
      this.cityInputControl.setValue(city);
    }

    // Other fields
    if (sublocalityLevel1) this.subLocalityLevel1 = sublocalityLevel1;
    if (adminLevel3) this.administrativeAreaLevel3 = adminLevel3;
    if (adminLevel1) this.administrativeAreaLevel1 = adminLevel1;

    if (country) {
      this.country = country;
      this.countryInputControl.setValue(country);
    }

    if (postalCode) {
      this.postalCode = postalCode;
      this.postalCodeInputControl.setValue(postalCode);
    }

    // Geometry / bounds / marker event
    if (!place.geometry?.location) return;

    const bounds = new google.maps.LatLngBounds();
    if (place.geometry.viewport) bounds.union(place.geometry.viewport);
    else bounds.extend(place.geometry.location);

    this.markerPositions = [
      {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      },
    ];

    const event: GemInputGoogleAddressEvent = new GemInputGoogleAddressEvent();
    event.bounds = bounds;
    event.markerPositions = this.markerPositions;

    this.change.emit(event);
  }

  private clearFields(): void {
    this.subLocalityLevel1 = "";
    this.city = "";
    this.administrativeAreaLevel3 = "";
    this.administrativeAreaLevel1 = "";
    this.country = "";
    this.postalCode = "";
  }

  trim() {
    this.formHelper.trimWhitespace(this.streetNameControl);
  }

  get isGoogleServicesConsent(): boolean {
    return this.cookieHelperService.isGoogleServicesContent();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
